/**
 * Pestaña "Cotizaciones": lista con búsqueda, estatus, Ver PDF / Editar /
 * WhatsApp / Eliminar / Convertir en contrato, y edición de los Textos del PDF.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency, STATUS_LABELS, displayName, displayPhone, serviceSummary } from "./utils";

export function QuotesTab({ quotesHook, DatePicker, userRole }) {
  const {
    filteredQuotes, quotesSearch, setQuotesSearch, loadingQuotes,
    pdfTextos, setPdfTextos, savingPdfTextos, savePdfTextos,
    editingQuote, startEdit, cancelEdit, setEditForm, saveEdit, regeneratePdfAfterEdit,
    viewPdf, sendWhatsApp, changeStatus, removeQuote, convertToContract,
  } = quotesHook;
  const [showTextos, setShowTextos] = useState(false);
  const quotes = filteredQuotes();

  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-pearl text-xl font-bold">Cotizaciones</h2>
          <p className="text-pearl-muted text-sm">Cotizaciones generadas desde el /cotizador público</p>
        </div>
        <Button variant="outline" className="btn-gold-outline" onClick={() => setShowTextos((v) => !v)} data-testid="toggle-pdf-textos-btn">
          {showTextos ? "Ocultar" : "Editar"} Textos del PDF
        </Button>
      </div>

      {showTextos && pdfTextos && (
        <Card className="card-premium border-gold/30">
          <CardHeader><CardTitle className="text-gold">Textos del PDF de Cotización</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {userRole !== "admin" && (
              <p className="text-amber-400 text-xs">Solo un usuario Admin puede guardar cambios en los textos del PDF. Puedes revisarlos, pero el botón de guardar está deshabilitado.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-pearl text-xs">Título flete local</Label>
                <Input value={pdfTextos.flete_local_titulo} className="input-premium" data-testid="pdf-texto-flete-local-titulo"
                  onChange={(e) => setPdfTextos({ ...pdfTextos, flete_local_titulo: e.target.value })} />
              </div>
              <div>
                <Label className="text-pearl text-xs">Título flete foráneo</Label>
                <Input value={pdfTextos.flete_foraneo_titulo} className="input-premium" data-testid="pdf-texto-flete-foraneo-titulo"
                  onChange={(e) => setPdfTextos({ ...pdfTextos, flete_foraneo_titulo: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-pearl text-xs">Texto flete según ubicación (local)</Label>
              <Textarea value={pdfTextos.flete_local_texto} className="input-premium" rows={2} data-testid="pdf-texto-flete-local-texto"
                onChange={(e) => setPdfTextos({ ...pdfTextos, flete_local_texto: e.target.value })} />
            </div>
            <div>
              <Label className="text-pearl text-xs">Texto flete según ubicación (foráneo)</Label>
              <Textarea value={pdfTextos.flete_foraneo_texto} className="input-premium" rows={2} data-testid="pdf-texto-flete-foraneo-texto"
                onChange={(e) => setPdfTextos({ ...pdfTextos, flete_foraneo_texto: e.target.value })} />
            </div>
            <div>
              <Label className="text-pearl text-xs">Nota de flete adicional</Label>
              <Textarea value={pdfTextos.flete_extra_texto} className="input-premium" rows={2} data-testid="pdf-texto-flete-extra"
                onChange={(e) => setPdfTextos({ ...pdfTextos, flete_extra_texto: e.target.value })} />
            </div>
            <div>
              <Label className="text-pearl text-xs">Título "Galería y privacidad"</Label>
              <Input value={pdfTextos.galeria_titulo} className="input-premium" data-testid="pdf-texto-galeria-titulo"
                onChange={(e) => setPdfTextos({ ...pdfTextos, galeria_titulo: e.target.value })} />
            </div>
            <div>
              <Label className="text-pearl text-xs">Texto "Galería y privacidad"</Label>
              <Textarea value={pdfTextos.galeria_texto} className="input-premium" rows={3} data-testid="pdf-texto-galeria-texto"
                onChange={(e) => setPdfTextos({ ...pdfTextos, galeria_texto: e.target.value })} />
            </div>
            <p className="text-pearl-muted text-xs">La leyenda "Precios netos, IVA incluido..." se edita en la pestaña <b>Precios</b> (campo Nota de precios).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-pearl text-xs">Nota de vigencia (*)</Label>
                <Input value={pdfTextos.nota_vigencia} className="input-premium" data-testid="pdf-texto-nota-vigencia"
                  onChange={(e) => setPdfTextos({ ...pdfTextos, nota_vigencia: e.target.value })} />
              </div>
              <div>
                <Label className="text-pearl text-xs">Nota de flete sujeto a confirmación (*)</Label>
                <Input value={pdfTextos.nota_flete_legal} className="input-premium" data-testid="pdf-texto-nota-flete-legal"
                  onChange={(e) => setPdfTextos({ ...pdfTextos, nota_flete_legal: e.target.value })} />
              </div>
              <div>
                <Label className="text-pearl text-xs">Nota de cambios (*)</Label>
                <Input value={pdfTextos.nota_cambios} className="input-premium" data-testid="pdf-texto-nota-cambios"
                  onChange={(e) => setPdfTextos({ ...pdfTextos, nota_cambios: e.target.value })} />
              </div>
              <div>
                <Label className="text-pearl text-xs">Nota "no es un contrato" (*)</Label>
                <Input value={pdfTextos.nota_no_contrato} className="input-premium" data-testid="pdf-texto-nota-no-contrato"
                  onChange={(e) => setPdfTextos({ ...pdfTextos, nota_no_contrato: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-pearl text-xs">Enlace del aviso de privacidad (QR del PDF y footers públicos)</Label>
              <Input value={pdfTextos.privacy_url} className="input-premium" data-testid="pdf-texto-privacy-url"
                onChange={(e) => setPdfTextos({ ...pdfTextos, privacy_url: e.target.value })} />
            </div>
            <div>
              <Label className="text-pearl text-xs">Pie de página</Label>
              <Input value={pdfTextos.pie_pagina} className="input-premium" data-testid="pdf-texto-pie-pagina"
                onChange={(e) => setPdfTextos({ ...pdfTextos, pie_pagina: e.target.value })} />
            </div>
            <Button className="btn-gold" disabled={savingPdfTextos || userRole !== "admin"} onClick={savePdfTextos} data-testid="guardar-pdf-textos-btn">
              {savingPdfTextos ? "Guardando..." : "Guardar Textos del PDF"}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="relative">
        <Input
          data-testid="quotes-search-input"
          type="text"
          placeholder="Buscar por folio (ej. COT-1500), nombre o teléfono..."
          value={quotesSearch}
          onChange={(e) => setQuotesSearch(e.target.value)}
          className="input-premium pl-10"
        />
        <svg className="w-5 h-5 text-pearl-muted absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"></path>
        </svg>
      </div>

      {loadingQuotes ? (
        <p className="text-pearl-muted text-center py-8">Cargando cotizaciones...</p>
      ) : quotes.length === 0 ? (
        <Card className="card-premium border-dashed">
          <CardContent className="p-8 text-center">
            <p className="text-pearl-muted" data-testid="quotes-no-results">
              {quotesSearch ? `No se encontraron cotizaciones para "${quotesSearch}"` : "No hay cotizaciones todavía"}
            </p>
          </CardContent>
        </Card>
      ) : (
        quotes.map((q) => (
          <Card key={q.id} className="card-premium" data-testid={`quote-card-${q.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-gold font-mono font-bold text-sm bg-gold/10 border border-gold/30 px-2 py-0.5 rounded" data-testid={`quote-folio-${q.id}`}>
                      {q.folio}
                    </span>
                    <Select value={q.status || "nueva"} onValueChange={(v) => changeStatus(q.id, v)}>
                      <SelectTrigger className="h-7 w-32 input-premium text-xs" data-testid={`quote-status-select-${q.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <h3 className="text-pearl font-bold">{displayName(q)}</h3>
                  <p className="text-pearl-muted text-sm">{[displayPhone(q), q.fecha_evento ? `Evento: ${q.fecha_evento}` : null].filter(Boolean).join(" · ")}</p>
                  <p className="text-pearl-muted/70 text-sm">{serviceSummary(q)}</p>
                </div>
                <div className="text-right">
                  <p className="text-gold font-bold text-2xl" data-testid={`quote-total-${q.id}`}>{formatCurrency(q.total)}</p>
                  <div className="flex gap-2 mt-2 flex-wrap justify-end">
                    <Button size="sm" className="btn-gold-outline" onClick={() => viewPdf(q)} data-testid={`quote-pdf-btn-${q.id}`}>Ver PDF</Button>
                    <Button size="sm" className="btn-gold-outline" onClick={() => startEdit(q)} data-testid={`quote-edit-btn-${q.id}`}>Editar</Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => sendWhatsApp(q)} data-testid={`quote-whatsapp-btn-${q.id}`}>WhatsApp</Button>
                    {q.status !== "aceptada" && (
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => convertToContract(q.id)} data-testid={`quote-convert-btn-${q.id}`}>Convertir en contrato</Button>
                    )}
                    {userRole === "admin" && (
                      <Button size="sm" variant="destructive" onClick={() => removeQuote(q.id)} data-testid={`quote-delete-btn-${q.id}`}>Eliminar</Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {editingQuote && (
        <Dialog open={!!editingQuote} onOpenChange={(open) => !open && cancelEdit()}>
          <DialogContent className="bg-night border-gold/30 text-pearl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="quote-edit-dialog">
            <DialogHeader>
              <DialogTitle className="text-gold">Editar Cotización {editingQuote.folio}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-pearl">Nombre</Label>
                  <Input value={editingQuote.form.nombre} className="input-premium" data-testid="quote-edit-nombre"
                    onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-pearl">Teléfono</Label>
                  <Input value={editingQuote.form.telefono} className="input-premium" data-testid="quote-edit-telefono"
                    onChange={(e) => setEditForm((f) => ({ ...f, telefono: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-pearl">Fecha del evento</Label>
                  {DatePicker ? (
                    <DatePicker value={editingQuote.form.fecha_evento} onChange={(d) => setEditForm((f) => ({ ...f, fecha_evento: d }))} placeholder="Fecha del evento" />
                  ) : (
                    <Input type="date" value={editingQuote.form.fecha_evento} className="input-premium" onChange={(e) => setEditForm((f) => ({ ...f, fecha_evento: e.target.value }))} />
                  )}
                </div>
                <div>
                  <Label className="text-pearl">Lugar / Salón</Label>
                  <Input value={editingQuote.form.salon} className="input-premium" data-testid="quote-edit-salon"
                    onChange={(e) => setEditForm((f) => ({ ...f, salon: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-pearl">Zona</Label>
                  <Select value={editingQuote.form.zona} onValueChange={(v) => setEditForm((f) => ({ ...f, zona: v }))}>
                    <SelectTrigger className="input-premium" data-testid="quote-edit-zona"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local">Chihuahua capital</SelectItem>
                      <SelectItem value="foraneo">Foráneo (fuera de la ciudad)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-pearl self-end pb-2">
                  <input type="checkbox" checked={editingQuote.form.factura} data-testid="quote-edit-factura"
                    onChange={(e) => setEditForm((f) => ({ ...f, factura: e.target.checked }))} />
                  Requiere factura
                </label>
              </div>

              <div className="border border-gold/20 rounded p-3 space-y-2">
                <label className="flex items-center gap-2 text-pearl font-semibold">
                  <input type="checkbox" checked={editingQuote.form.cabina_selected} data-testid="quote-edit-cabina-selected"
                    onChange={(e) => setEditForm((f) => ({ ...f, cabina_selected: e.target.checked }))} />
                  Cabina de Fotos
                </label>
                {editingQuote.form.cabina_selected && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-pearl-muted text-xs">Nombre del paquete</Label>
                      <Input placeholder="Nombre del paquete" value={editingQuote.form.cabina_label} className="input-premium" data-testid="quote-edit-cabina-label"
                        onChange={(e) => setEditForm((f) => ({ ...f, cabina_label: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-pearl-muted text-xs">Precio</Label>
                      <Input type="number" placeholder="Precio" value={editingQuote.form.cabina_precio} className="input-premium" data-testid="quote-edit-cabina-precio"
                        onChange={(e) => setEditForm((f) => ({ ...f, cabina_precio: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-pearl-muted text-xs">Horas</Label>
                      <Input type="number" placeholder="Horas" value={editingQuote.form.cabina_hours} className="input-premium" data-testid="quote-edit-cabina-hours"
                        onChange={(e) => setEditForm((f) => ({ ...f, cabina_hours: e.target.value }))} />
                    </div>
                    <label className="flex items-center gap-2 text-pearl-muted text-sm self-end pb-2">
                      <input type="checkbox" checked={editingQuote.form.cabina_keychain}
                        onChange={(e) => setEditForm((f) => ({ ...f, cabina_keychain: e.target.checked }))} />
                      Incluye llaveros
                    </label>
                  </div>
                )}
              </div>

              <div className="border border-gold/20 rounded p-3 space-y-2">
                <label className="flex items-center gap-2 text-pearl font-semibold">
                  <input type="checkbox" checked={editingQuote.form.video360_selected} data-testid="quote-edit-video360-selected"
                    onChange={(e) => setEditForm((f) => ({ ...f, video360_selected: e.target.checked }))} />
                  Video 360°
                </label>
                {editingQuote.form.video360_selected && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-pearl-muted text-xs">Nombre del paquete</Label>
                      <Input placeholder="Nombre del paquete" value={editingQuote.form.video360_label} className="input-premium" data-testid="quote-edit-video360-label"
                        onChange={(e) => setEditForm((f) => ({ ...f, video360_label: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-pearl-muted text-xs">Precio</Label>
                      <Input type="number" placeholder="Precio" value={editingQuote.form.video360_precio} className="input-premium" data-testid="quote-edit-video360-precio"
                        onChange={(e) => setEditForm((f) => ({ ...f, video360_precio: e.target.value }))} />
                    </div>
                    <label className="flex items-center gap-2 text-pearl-muted text-sm self-end pb-2">
                      <input type="checkbox" checked={editingQuote.form.video360_nieve}
                        onChange={(e) => setEditForm((f) => ({ ...f, video360_nieve: e.target.checked }))} />
                      Variante con nieve
                    </label>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-pearl">Subtotal</Label>
                  <Input type="number" value={editingQuote.form.subtotal} className="input-premium" data-testid="quote-edit-subtotal"
                    onChange={(e) => setEditForm((f) => ({ ...f, subtotal: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-pearl">Total</Label>
                  <Input type="number" value={editingQuote.form.total} className="input-premium" data-testid="quote-edit-total"
                    onChange={(e) => setEditForm((f) => ({ ...f, total: e.target.value }))} />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" className="text-pearl-muted" onClick={cancelEdit}>Cancelar</Button>
                <Button className="btn-gold-outline" onClick={regeneratePdfAfterEdit} data-testid="quote-edit-regenerate-pdf-btn">Regenerar PDF</Button>
                <Button className="btn-gold" onClick={saveEdit} data-testid="quote-edit-save-btn">Guardar Cambios</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
